import tsRest from "ts-rest-client";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { every, find, groupBy, isNil, size, sortBy, transform } from "lodash";
import { getMomentTZDateTime } from "ts-util";
import TranslatorManager from "ts-translate-js";

const tr = TranslatorManager.boundTR;

export function ScheduleRangeSelector({ eventDate, sellerId, onChangeScheduleRange, onControlLoaded, alertMessageClasses, comboClasses, onContentChange }) {
    const [availableRanges, setAvailableRanges] = useState([]);
    const [availableDates, setAvailableDates] = useState({});
    const [dateComboItems, setDateComboItems] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [warningMsg, setWarningMsg] = useState("");

    useEffect(() => {
        const loadScheduleRanges = async () => {
            try {
                const url = `/eventdates/${eventDate.EventDateID}/selleruser/${sellerId}/scheduleranges`;
                const scheduleRanges = await tsRest.get(url);
                setAvailableRanges(scheduleRanges);
    
                // Aller chercher toutes les dates unique dans les ranges
                if (!isNil(scheduleRanges) && scheduleRanges !== "" && scheduleRanges.length > 0) {
                    // Grouper les ranges par date
                    const _availableDates = groupBy(scheduleRanges, "ValidDate");
                    setAvailableDates(_availableDates);
    
                    // Extraire les données pour le combo de sélection des dates
                    // Afficher message complet si tous les ranges sont complets
                    const allDates = Object.keys(_availableDates);
                    let dateItems = transform(
                        allDates,
                        function (result, dateItem) {
                            const rangesForSelectedDate = _availableDates[dateItem];
                            const isDateComplete = every(rangesForSelectedDate, "IsComplete");
                            result.push({ dateValue: dateItem, isDateComplete: isDateComplete });
                            return true;
                        },
                        []
                    );
    
                    // Trier les dates avant de les mettre en state
                    const sortedDateItems = sortBy(dateItems, "dateValue");
                    setDateComboItems(sortedDateItems);
    
                    // Sélectionner la première date par défaut
                    const firstDate = sortedDateItems[0].dateValue;
                    setSelectedDate(firstDate);
                } else {
                    setWarningMsg(tr("Aucune plage horaire configurée pour cet événement."));
                }

                if(onControlLoaded) onControlLoaded();
                
            } catch (err) {
                console.error(err);
                setWarningMsg(tr("Une erreur est survenue lors du chargement des plages disponibles."));
            }
        };
    
        loadScheduleRanges();
    }, []);

    useEffect(() => {
        if(onContentChange) onContentChange();
    });


    const handleDateChangeEvent = (event) => {
        // Setter la date dans les states
        setSelectedDate(event.target.value);
    };

    const handleRangeChangeEvent = (event) => {
        // Aller le chercher dans les available
        const transactionTagSelected = event.target.value;
        const selectedRange = find(availableRanges, ["TransactionTag", transactionTagSelected]);

        // Valider que le range sélectionné n'est pas complet
        if (!selectedRange || selectedRange.IsComplete) {
            setWarningMsg(tr("La plage horaire sélectionnée n'est pas disponible."));
            return;
        }

        // Clear warning message
        setWarningMsg("");

        // Retourner le range sélectionné au contrôle parent
        onChangeScheduleRange(selectedRange);
    };

    const dateDisplayFormat = (dateValue, TZ) => {
        const formatStr = tr("D MMMM YYYY");
        const dateToDisplay = getMomentTZDateTime(dateValue, TZ.TZDatabaseID).format(formatStr);
        return dateToDisplay;
    };

    const getDateDisplayText = (dateItem, TZ) => {
        const dateText = dateDisplayFormat(dateItem.dateValue, TZ);
        // Afficher message complet si tous les ranges sont complets
        return dateText + " " + (dateItem.isDateComplete ? tr("(Complet)") : "");
    };

    let scheduleDateSelectionCombo = null;
    let scheduleRangeSelectionCombo = null;
    if (!isNil(availableDates) && size(availableDates) > 0 && !isNil(dateComboItems) && size(dateComboItems) > 0) {
        scheduleDateSelectionCombo = (
            <>
                <div>
                    <h5>
                        <strong>{tr("Sélectionnez une date")}</strong>
                    </h5>
                </div>
                <div>
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                        <select className={comboClasses} name="DateSelectionCombo" onChange={handleDateChangeEvent} defaultValue={selectedDate}>
                            {dateComboItems.map((dateItem) => (
                                <option key={dateItem.dateValue} value={dateItem.dateValue}>
                                    {getDateDisplayText(dateItem, eventDate.TimeZone)}
                                </option>
                            ))}
                            ;
                        </select>
                    </div>
                </div>
            </>
        );

        // Trier les plages horaires avant de les afficher
        let sortedRangesforDate = [];
        if (selectedDate) {
            sortedRangesforDate = sortBy(availableDates[selectedDate], ["ValidStartDateTime"]);
        }

        const headerMessage = tr("Veuillez sélectionner une plage horaire");
        scheduleRangeSelectionCombo = (
            <>
                <div>
                    <h5>
                        <strong>{tr("Sélectionnez une plage horaire")}</strong>
                    </h5>
                </div>
                <div>
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/no-onchange */}
                        <select className={comboClasses} name="RangeSelectionCombo" onChange={handleRangeChangeEvent}>
                            <option key="header" value="">
                                {headerMessage}
                            </option>
                            {sortedRangesforDate.map((rangeItem, i) => (
                                <option key={i} value={rangeItem.TransactionTag}>
                                    {rangeItem.StartTime} - {rangeItem.EndTime} {rangeItem.IsComplete ? tr("(Complet)") : ""}
                                </option>
                            ))}                            
                        </select>
                    </div>
                </div>
            </>
        );
    }

    let warningMessageSection = null;
    if (warningMsg && warningMsg !== "") {
        warningMessageSection = <div className={alertMessageClasses}>{warningMsg}</div>;
    }

    return (
        <>
            {scheduleDateSelectionCombo}
            {scheduleRangeSelectionCombo}
            {warningMessageSection}
        </>
    );
}

ScheduleRangeSelector.propTypes = {
    eventDate: PropTypes.object,
    sellerId: PropTypes.number,
    onChangeScheduleRange: PropTypes.func,
    onControlLoaded: PropTypes.func,
    alertMessageClasses: PropTypes.string,
    comboClasses: PropTypes.string,
    onContentChange: PropTypes.func,
};
